// src/utilies/drivewayTools/Add.js
import {
  ScreenSpaceEventHandler,
  ScreenSpaceEventType,
  JulianDate,
  Color,
  CallbackProperty,
  Ellipsoid,
  Cartesian2,
  Cartesian3,
  Matrix4,
  Transforms,
  ConstantProperty,
  PropertyBag,
  Cartographic,
  Math as CesiumMath,
} from "cesium";

import Type from "../Types";
import { getUsernameAndUserIdFormat, showToast } from "../Utility";
import { disableCameraControls, restoreCameraControls } from "../MapUtility";

const timeNow = () => JulianDate.now();

// Same-point tolerance (meters)
const SAME_POINT_EPS_METERS = 0.25;

// Free draw capture (pencil)
const DRAW_MIN_STEP_METERS = 0.12;
const DRAW_MAX_POINTS = 700;

// Area selection (polygon) for BOTH modes (pencil polygon or rectangle polygon)
const POLY_DECIMATE_STEP_METERS = 0.2;
const POLY_BBOX_PAD_METERS = 0.75;
const ENDPOINT_CACHE_REBUILD_MS = 500;
const TURN_DIR_INVERT = true;
const SMOOTH_ITERATIONS_VISUAL = 2;

// Persisted visuals
const PERSIST_DECIMATE_STEP_METERS = 0.2;
const PERSIST_SMOOTH_ITERS = 1;

// Safety caps
const MAX_POINTS_PER_STROKE = 40;

// Modes
const MODE_RECT = "rect";
const MODE_PENCIL = "pencil";

const safeNowMs = () => (typeof performance !== "undefined" && performance.now ? performance.now() : Date.now());

const windowPosToCartesian = (viewer, winPos) => {
  if (!viewer || !winPos) return null;
  const ray = viewer.camera.getPickRay(winPos);
  const globe = viewer.scene.globe;
  return globe?.pick?.(ray, viewer.scene) || viewer.camera.pickEllipsoid(winPos, Ellipsoid.WGS84);
};

const getPolylinePositions = (entity) => {
  const pl = entity?.polyline;
  if (!pl?.positions) return null;

  const v = pl.positions;
  const t = timeNow();

  if (typeof v?.getValue === "function") return v.getValue(t);
  if (Array.isArray(v)) return v;
  return null;
};

const getEndpointTangent = (positions, whichEnd) => {
  if (!positions || positions.length < 2) return null;

  let from;
  let to;

  if (whichEnd === "start") {
    from = positions[0];
    to = positions[1];
  } else {
    from = positions[positions.length - 2];
    to = positions[positions.length - 1];
  }

  if (!from || !to) return null;

  const dir = Cartesian3.subtract(to, from, new Cartesian3());
  const mag = Cartesian3.magnitude(dir);
  if (!Number.isFinite(mag) || mag <= 1e-9) return null;

  return Cartesian3.normalize(dir, new Cartesian3());
};

const getNumericPropValue = (entity, propName) => {
  const pbag = entity?.properties;
  if (!pbag) return null;

  const prop = pbag[propName];
  if (!prop) return null;

  try {
    const v = typeof prop.getValue === "function" ? prop.getValue(timeNow()) : prop;
    const n = typeof v === "number" ? v : Number(String(v).trim());
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
};

const collectIdsFromGeoJSON = (gjson, out = new Set()) => {
  if (!gjson?.features || !Array.isArray(gjson.features)) return out;

  for (const f of gjson.features) {
    const n = Number(f?.properties?.id);
    if (Number.isFinite(n)) out.add(n);
  }

  return out;
};

const collectIdsFromDataSource = (ds, out = new Set()) => {
  const ents = ds?.entities?.values || [];

  for (const ent of ents) {
    const n = getNumericPropValue(ent, "id");
    if (Number.isFinite(n)) out.add(n);
  }

  return out;
};

const normalizeLayerIdentity = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[\s\-_]/g, "");

const findMapObjectByDataSource = (ds, stLocal) => {
  const list = Array.isArray(stLocal?.mapObjectsRef?.current) ? stLocal.mapObjectsRef.current : [];

  return (
    list.find((o) => {
      if (!o || !ds) return false;
      if (o.dataSource === ds || o.laneletDataSource === ds) return true;
      if (ds.layerId != null && o.layer_id != null && String(o.layer_id) === String(ds.layerId)) return true;

      const a = normalizeLayerIdentity(o.file_name || o.layer_name || o.name);
      const b = normalizeLayerIdentity(ds.layerName || ds.name);
      return a && b && a === b;
    }) || null
  );
};

const getNextIdPlus2 = (ds, stLocal = null) => {
  const layer = findMapObjectByDataSource(ds, stLocal);
  const ids = new Set();

  for (const gjson of [layer?.geojson, layer?.fullGeojson, layer?.hiddenGeojson, layer?.original_geojson, layer?.lenelet_geojson]) {
    collectIdsFromGeoJSON(gjson, ids);
  }

  collectIdsFromDataSource(ds, ids);

  const stored = Number(layer?.bboxMaxFeaturePropertiesId ?? layer?.bboxMaxId);
  const lastId = Number(layer?.geojson_last_id);

  let maxId = 0;
  if (Number.isFinite(stored)) maxId = Math.max(maxId, stored);
  if (Number.isFinite(lastId)) maxId = Math.max(maxId, lastId);

  for (const id of ids) {
    if (id > maxId) maxId = id;
  }

  let next = maxId + 2;
  while (ids.has(next)) {
    next += 2;
  }

  if (layer) {
    layer.geojson_last_id = next;
    layer.bboxMaxId = next;
    layer.bboxMaxFeaturePropertiesId = next;
  }

  const patch = (o) => {
    if (!o || !ds) return o;

    const sameLayer =
      o === layer ||
      o.dataSource === ds ||
      o.laneletDataSource === ds ||
      (ds.layerId != null && o.layer_id != null && String(o.layer_id) === String(ds.layerId)) ||
      (normalizeLayerIdentity(o.file_name || o.layer_name || o.name) && normalizeLayerIdentity(o.file_name || o.layer_name || o.name) === normalizeLayerIdentity(ds.layerName || ds.name));

    if (!sameLayer) return o;

    return {
      ...o,
      geojson_last_id: next,
      bboxMaxId: next,
      bboxMaxFeaturePropertiesId: next,
    };
  };

  if (Array.isArray(stLocal?.mapObjectsRef?.current)) {
    stLocal.mapObjectsRef.current = stLocal.mapObjectsRef.current.map(patch);
  }

  if (typeof stLocal?.setMapObjects === "function") {
    stLocal.setMapObjects((prev) => (Array.isArray(prev) ? prev.map(patch) : prev));
  }

  return Number.isFinite(next) ? next : 2;
};

const computeLengthMeters = (positions) => {
  if (!Array.isArray(positions) || positions.length < 2) return 0;
  let sum = 0;
  for (let i = 1; i < positions.length; i++) {
    const a = positions[i - 1];
    const b = positions[i];
    if (!a || !b) continue;
    sum += Cartesian3.distance(a, b);
  }
  return sum;
};

const pickPolylineStyleFromDataSource = (ds) => {
  const t = timeNow();
  const ents = ds?.entities?.values;
  if (!Array.isArray(ents) || ents.length === 0) return null;

  const first = ents.find((e) => e?.polyline);
  if (!first?.polyline) return null;

  const pl = first.polyline;
  const getVal = (p) => (typeof p?.getValue === "function" ? p.getValue(t) : p);

  return {
    width: getVal(pl.width),
    clampToGround: getVal(pl.clampToGround),
    material: pl.material,
    arcType: getVal(pl.arcType),
    zIndex: getVal(pl.zIndex),
  };
};

const addTurningLineEntity = (turningLineDs, a, b, name, numericId, baseStyle, turnDirection) => {
  if (!turningLineDs) return null;

  const positions = [a, b];

  const td = turnDirection === "left" || turnDirection === "right" ? turnDirection : "right";

  const props = new PropertyBag({});
  props.addProperty?.("id");
  props.addProperty?.("turn_direction");

  props.id = new ConstantProperty(numericId);
  props.turn_direction = new ConstantProperty(td);

  const creator = getUsernameAndUserIdFormat(_state?.userData);
  if (creator) {
    props.addProperty?.("creator");
    props.creator = new ConstantProperty(creator);
  }

  const entId = `${name}-${numericId}-${Date.now()}-${Math.random().toString(16).slice(2)}`;

  return turningLineDs.entities.add({
    id: entId,
    name,
    properties: props,
    polyline: {
      positions,
      width: baseStyle?.width ?? 3,
      material: baseStyle?.material,
      clampToGround: baseStyle?.clampToGround ?? true,
      arcType: baseStyle?.arcType,
      zIndex: baseStyle?.zIndex,
    },
  });
};

const addVirtualLineEntity = (virtualLineDs, a, b, name, numericId, baseStyle) => {
  if (!virtualLineDs) return null;

  const positions = [a, b];

  const props = new PropertyBag({});
  props.addProperty?.("id");

  props.id = new ConstantProperty(numericId);

  const creator = getUsernameAndUserIdFormat(_state?.userData);
  if (creator) {
    props.addProperty?.("creator");
    props.creator = new ConstantProperty(creator);
  }

  const entId = `${name}-${numericId}-${Date.now()}-${Math.random().toString(16).slice(2)}`;

  return virtualLineDs.entities.add({
    id: entId,
    name,
    properties: props,
    polyline: {
      positions,
      width: baseStyle?.width ?? 3,
      material: baseStyle?.material,
      clampToGround: baseStyle?.clampToGround ?? true,
      arcType: baseStyle?.arcType,
      zIndex: baseStyle?.zIndex,
    },
  });
};

// Lanelet mid-line creation
const midpoint = (a, b) => Cartesian3.midpoint(a, b, new Cartesian3());

const computeMidlineBetweenTwoSegments = (rightSegPositions, leftSegPositions) => {
  if (!Array.isArray(rightSegPositions) || rightSegPositions.length < 2) return null;
  if (!Array.isArray(leftSegPositions) || leftSegPositions.length < 2) return null;

  const r0 = rightSegPositions[0];
  const r1 = rightSegPositions[rightSegPositions.length - 1];
  const l0 = leftSegPositions[0];
  const l1 = leftSegPositions[leftSegPositions.length - 1];
  if (!r0 || !r1 || !l0 || !l1) return null;

  if (dist2D(r0, l0) < dist2D(r0, l1)) {
    return [midpoint(r0, l0), midpoint(r1, l1)];
  } else {
    return [midpoint(r0, l0), midpoint(r1, l1)];
  }
};

const getTurnDirectionFromTwoLines = (rightPositions, leftPositions, depPair, arrPair) => {
  // Fallbacks keep old behavior if pairs are missing
  if (!depPair?.aPoint || !depPair?.bPoint || !arrPair?.aPoint || !arrPair?.bPoint) {
    const r0 = rightPositions?.[0];
    const r1 = rightPositions?.[rightPositions.length - 1];
    const l0 = leftPositions?.[0];
    const l1 = leftPositions?.[leftPositions.length - 1];
    if (!r0 || !r1 || !l0 || !l1) return "right";

    const rightLen = Cartesian3.distance(r0, r1);
    const leftLen = Cartesian3.distance(l0, l1);

    if (!Number.isFinite(rightLen) || !Number.isFinite(leftLen)) return "right";
    if (leftLen < rightLen) return "left";
    if (rightLen < leftLen) return "right";
    return "right";
  }

  // ---- Direction by geometry (ENU cross product) ----
  const depMid = Cartesian3.midpoint(depPair.aPoint, depPair.bPoint, new Cartesian3());
  const arrMid = Cartesian3.midpoint(arrPair.aPoint, arrPair.bPoint, new Cartesian3());

  // Approach vector: depPair direction, oriented toward arrival
  const depDirA = Cartesian3.subtract(depPair.bPoint, depPair.aPoint, new Cartesian3());
  const depDirB = Cartesian3.subtract(depPair.aPoint, depPair.bPoint, new Cartesian3());
  const towardArr = Cartesian3.subtract(arrMid, depMid, new Cartesian3());

  const dotA = Cartesian3.dot(depDirA, towardArr);
  const approach = dotA >= 0 ? depDirA : depDirB;

  // Turn vector: from dep midpoint to arr midpoint
  const turnVec = towardArr;

  // Local ENU at depMid
  const enu = Transforms.eastNorthUpToFixedFrame(depMid);
  const inv = Matrix4.inverse(enu, new Matrix4());

  const to2 = (vec) => {
    const end = Cartesian3.add(depMid, vec, new Cartesian3());
    const tmp = new Cartesian3();
    Matrix4.multiplyByPoint(inv, end, tmp);
    return { x: tmp.x, y: tmp.y };
  };

  const a2 = to2(approach);
  const t2 = to2(turnVec);

  const crossZ = a2.x * t2.y - a2.y * t2.x;

  // crossZ>0 => left, crossZ<0 => right
  if (TURN_DIR_INVERT) return crossZ > 0 ? "right" : "left";
  return crossZ > 0 ? "left" : "right";
};

const addLaneletCenterLineEntity = (laneletDs, a, b, numericId, rightLineId, leftLineId, turnDirection, lineType, baseStyle) => {
  if (!laneletDs || !a || !b || !Number.isFinite(numericId)) return null;

  const lt = typeof lineType === "string" && lineType.length ? lineType : "turning";
  const td = typeof turnDirection === "string" && turnDirection.length ? turnDirection : "right";

  const props = new PropertyBag({});
  const addP = (k) => props.addProperty?.(k);

  addP("id");
  addP("right_line_id");
  addP("left_line_id");
  addP("right_line_type");
  addP("left_line_type");
  addP("participant");
  addP("surface");
  addP("one_way");
  addP("subtype");
  addP("function");
  addP("speed_limit");
  addP("turn_direction");

  props.id = new ConstantProperty(numericId);
  props.right_line_id = new ConstantProperty(rightLineId);
  props.left_line_id = new ConstantProperty(leftLineId);
  props.right_line_type = new ConstantProperty(lt);
  props.left_line_type = new ConstantProperty(lt);
  props.participant = new ConstantProperty("all-vehicles");
  props.surface = new ConstantProperty("asphalt");
  props.one_way = new ConstantProperty("yes");
  props.subtype = new ConstantProperty("road");
  props.function = new ConstantProperty("turn");
  props.speed_limit = new ConstantProperty("10mph");
  props.turn_direction = new ConstantProperty(td);

  const creator = getUsernameAndUserIdFormat(_state?.userData);
  if (creator) {
    addP("creator");
    props.creator = new ConstantProperty(creator);
  }

  const entId = `lanelet-turn-center-${numericId}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  const style = baseStyle || null;

  return laneletDs.entities.add({
    id: entId,
    name: "lanelet_turn_center",
    properties: props,
    polyline: {
      positions: [a, b],
      width: style?.width ?? 2,
      material: style?.material ?? Color.WHITE.withAlpha(0.95),
      clampToGround: style?.clampToGround ?? true,
      arcType: style?.arcType,
      zIndex: style?.zIndex,
    },
  });
};

const dedupeCandidatesByPoint = (cands, epsMeters = SAME_POINT_EPS_METERS) => {
  if (!Array.isArray(cands) || cands.length === 0) return [];

  const out = [];
  for (let i = 0; i < cands.length; i++) {
    const c = cands[i];
    if (!c?.point) continue;

    let dupIdx = -1;
    for (let j = 0; j < out.length; j++) {
      const o = out[j];
      if (!o?.point) continue;
      const d = Cartesian3.distance(o.point, c.point);
      if (Number.isFinite(d) && d <= epsMeters) {
        dupIdx = j;
        break;
      }
    }

    if (dupIdx === -1) {
      out.push(c);
      continue;
    }

    const keep = out[dupIdx];
    if (Number.isFinite(c.dist) && (!Number.isFinite(keep.dist) || c.dist < keep.dist)) keep.dist = c.dist;
    if (Number.isFinite(c.s) && (!Number.isFinite(keep.s) || c.s < keep.s)) keep.s = c.s;
    if (!keep.tangent && c.tangent) keep.tangent = c.tangent;
  }

  return out;
};

const chaikinSmooth = (pts, iterations) => {
  if (!Array.isArray(pts) || pts.length < 3) return pts || [];

  let cur = pts.slice();
  for (let it = 0; it < iterations; it++) {
    if (cur.length < 3) break;

    const out = [cur[0]];
    for (let i = 0; i < cur.length - 1; i++) {
      const p0 = cur[i];
      const p1 = cur[i + 1];
      const q = Cartesian3.lerp(p0, p1, 0.25, new Cartesian3());
      const r = Cartesian3.lerp(p0, p1, 0.75, new Cartesian3());
      out.push(q, r);
    }
    out.push(cur[cur.length - 1]);

    cur = out;
    if (cur.length > DRAW_MAX_POINTS * 6) break;
  }
  return cur;
};

const decimateByDistance = (pts, stepMeters) => {
  if (!Array.isArray(pts) || pts.length === 0) return [];
  if (pts.length === 1) return [pts[0]];

  const out = [pts[0]];
  let last = pts[0];

  for (let i = 1; i < pts.length; i++) {
    const p = pts[i];
    if (!p) continue;
    if (Cartesian3.distance(last, p) >= stepMeters) {
      out.push(p);
      last = p;
    }
  }

  if (out.length === 1 || out[out.length - 1] !== pts[pts.length - 1]) out.push(pts[pts.length - 1]);
  return out;
};

const buildEndpointCache = (dsList) => {
  const items = [];
  const t = timeNow();

  for (let i = 0; i < dsList.length; i++) {
    const ds = dsList[i];
    if (!ds) continue;

    const ents = ds.entities?.values || [];
    for (let j = 0; j < ents.length; j++) {
      const e = ents[j];
      const positions = getPolylinePositions(e);
      if (!positions || positions.length < 2) continue;

      const start = positions[0];
      const end = positions[positions.length - 1];

      items.push({
        point: start,
        lineEntity: e,
        endType: "start",
        positions,
        tangent: getEndpointTangent(positions, "start"),
        _time: t,
      });

      items.push({
        point: end,
        lineEntity: e,
        endType: "end",
        positions,
        tangent: getEndpointTangent(positions, "end"),
        _time: t,
      });
    }
  }

  return items;
};

const distPointToSegment2D = (px, py, ax, ay, bx, by) => {
  const abx = bx - ax;
  const aby = by - ay;
  const apx = px - ax;
  const apy = py - ay;

  const ab2 = abx * abx + aby * aby;
  if (ab2 <= 1e-12) return { d: Math.hypot(px - ax, py - ay), t: 0 };

  let t = (apx * abx + apy * aby) / ab2;
  if (t < 0) t = 0;
  if (t > 1) t = 1;

  const cx = ax + t * abx;
  const cy = ay + t * aby;

  return { d: Math.hypot(px - cx, py - cy), t };
};

const projectPointToStroke = (px, py, segs) => {
  if (!Array.isArray(segs) || segs.length === 0) return { dist: Infinity, s: 0 };

  let bestD = Infinity;
  let bestS = 0;

  for (let i = 0; i < segs.length; i++) {
    const s = segs[i];
    const r = distPointToSegment2D(px, py, s.ax, s.ay, s.bx, s.by);
    if (r.d < bestD) {
      bestD = r.d;
      bestS = s.s0 + r.t * s.len;
    }
  }

  return { dist: bestD, s: bestS };
};

const computeStrokeCenterOnSurface = (strokePts) => {
  if (!Array.isArray(strokePts) || strokePts.length === 0) return null;

  const sum = new Cartesian3(0, 0, 0);
  let n = 0;

  for (let i = 0; i < strokePts.length; i++) {
    const p = strokePts[i];
    if (!p) continue;
    sum.x += p.x;
    sum.y += p.y;
    sum.z += p.z;
    n++;
  }

  if (n <= 0) return null;

  const avg = new Cartesian3(sum.x / n, sum.y / n, sum.z / n);
  const surf = Ellipsoid.WGS84.scaleToGeodeticSurface(avg, new Cartesian3());
  return surf || avg;
};

const makeStrokeLocal2D = (strokePts) => {
  if (!Array.isArray(strokePts) || strokePts.length < 2) return null;

  const origin = computeStrokeCenterOnSurface(strokePts) || strokePts[0];
  const enu = Transforms.eastNorthUpToFixedFrame(origin);
  const inv = Matrix4.inverse(enu, new Matrix4());

  const scratch = new Cartesian3();
  const pts2 = new Array(strokePts.length);

  let minx = Infinity,
    miny = Infinity,
    maxx = -Infinity,
    maxy = -Infinity;

  let valid = 0;

  for (let i = 0; i < strokePts.length; i++) {
    const c = strokePts[i];
    if (!c) continue;
    Matrix4.multiplyByPoint(inv, c, scratch);
    const x = scratch.x;
    const y = scratch.y;

    pts2[i] = { x, y };
    valid++;

    if (x < minx) minx = x;
    if (y < miny) miny = y;
    if (x > maxx) maxx = x;
    if (y > maxy) maxy = y;
  }

  if (valid < 2 || !Number.isFinite(minx) || !Number.isFinite(miny) || !Number.isFinite(maxx) || !Number.isFinite(maxy)) return null;

  const segs = [];
  let cum = 0;
  for (let i = 1; i < pts2.length; i++) {
    const a = pts2[i - 1];
    const b = pts2[i];
    if (!a || !b) continue;
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    if (len <= 1e-9) continue;
    segs.push({ ax: a.x, ay: a.y, bx: b.x, by: b.y, len, s0: cum });
    cum += len;
  }

  return { inv, segs, pts2, bbox: { minx, miny, maxx, maxy } };
};

const pointInPolygon2D = (x, y, poly) => {
  if (!Array.isArray(poly) || poly.length < 3) return false;

  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i]?.x;
    const yi = poly[i]?.y;
    const xj = poly[j]?.x;
    const yj = poly[j]?.y;
    if (!Number.isFinite(xi) || !Number.isFinite(yi) || !Number.isFinite(xj) || !Number.isFinite(yj)) continue;

    const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi + 1e-15) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
};

const normalizePolygonPoints = (pts2) => {
  const poly = [];
  if (!Array.isArray(pts2)) return poly;

  for (let i = 0; i < pts2.length; i++) {
    const p = pts2[i];
    if (!p || !Number.isFinite(p.x) || !Number.isFinite(p.y)) continue;
    poly.push({ x: p.x, y: p.y });
  }

  if (poly.length < 3) return poly;

  const a = poly[0];
  const b = poly[poly.length - 1];
  const close = Math.hypot(a.x - b.x, a.y - b.y) <= 1e-6;
  if (!close) poly.push({ x: a.x, y: a.y });

  return poly;
};

const makePairsFromOrderedPoints = (orderedPoints) => {
  const pairs = [];
  if (!Array.isArray(orderedPoints) || orderedPoints.length < 2) return pairs;

  for (let i = 0; i < orderedPoints.length - 1; i++) {
    const a = orderedPoints[i];
    const b = orderedPoints[i + 1];
    if (!a?.point || !b?.point) continue;
    pairs.push({
      a,
      b,
      aPoint: a.point,
      bPoint: b.point,
    });
  }

  return pairs;
};

const dist2D = (p1, p2) => {
  const dx = p1.x - p2.x;
  const dy = p1.y - p2.y;
  return Math.sqrt(dx * dx + dy * dy);
};

const minMaxLinksBetweenTwoPairs = (depPair, arrPair) => {
  const d0 = depPair?.aPoint;
  const d1 = depPair?.bPoint;
  const a0 = arrPair?.aPoint;
  const a1 = arrPair?.bPoint;
  if (!d0 || !d1 || !a0 || !a1) return null;

  const links = [
    { from: d0, to: a0, len: dist2D(d0, a0), di: 0, ai: 0 },
    { from: d0, to: a1, len: dist2D(d0, a1), di: 0, ai: 1 },
    { from: d1, to: a0, len: dist2D(d1, a0), di: 1, ai: 0 },
    { from: d1, to: a1, len: dist2D(d1, a1), di: 1, ai: 1 },
  ];

  // پیدا کردن بردارهای کم و زیاد
  const candMin = links.reduce((p, c) => (c.len < p.len ? c : p), links[0]);
  const candMax = links.reduce((p, c) => (c.len > p.len ? c : p), links[0]);

  const A0 = links.find((l) => l.di === 0 && l.ai === 0);
  const A1 = links.find((l) => l.di === 1 && l.ai === 1);
  const B0 = links.find((l) => l.di === 0 && l.ai === 1);
  const B1 = links.find((l) => l.di === 1 && l.ai === 0);
  if (!A0 || !A1 || !B0 || !B1) return { right: candMin, left: candMax, all: links };

  // مختصات محلی ENU
  const origin = Cartesian3.midpoint(d0, d1, new Cartesian3());
  const enu = Transforms.eastNorthUpToFixedFrame(origin);
  const inv = Matrix4.inverse(enu, new Matrix4());

  const to2 = (p) => {
    const tmp = new Cartesian3();
    Matrix4.multiplyByPoint(inv, p, tmp);
    return { x: tmp.x, y: tmp.y };
  };

  const EPS = 1e-9;
  const sgn = (v) => (v > EPS ? 1 : v < -EPS ? -1 : 0);
  const orient = (p, q, r) => (q.x - p.x) * (r.y - p.y) - (q.y - p.y) * (r.x - p.x);
  const onSeg = (p, q, r) => r.x >= Math.min(p.x, q.x) - EPS && r.x <= Math.max(p.x, q.x) + EPS && r.y >= Math.min(p.y, q.y) - EPS && r.y <= Math.max(p.y, q.y) + EPS;

  const segIntersect = (p1, p2, q1, q2) => {
    const o1 = orient(p1, p2, q1);
    const o2 = orient(p1, p2, q2);
    const o3 = orient(q1, q2, p1);
    const o4 = orient(q1, q2, p2);

    const s1 = sgn(o1);
    const s2 = sgn(o2);
    const s3 = sgn(o3);
    const s4 = sgn(o4);

    if (s1 === 0 && onSeg(p1, p2, q1)) return true;
    if (s2 === 0 && onSeg(p1, p2, q2)) return true;
    if (s3 === 0 && onSeg(q1, q2, p1)) return true;
    if (s4 === 0 && onSeg(q1, q2, p2)) return true;

    return s1 * s2 < 0 && s3 * s4 < 0;
  };

  const A_cross = segIntersect(to2(A0.from), to2(A0.to), to2(A1.from), to2(A1.to));
  const B_cross = segIntersect(to2(B0.from), to2(B0.to), to2(B1.from), to2(B1.to));

  const sumA = A0.len + A1.len;
  const sumB = B0.len + B1.len;

  let pick0;
  let pick1;

  if (A_cross !== B_cross) {
    [pick0, pick1] = A_cross ? [B0, B1] : [A0, A1];
  } else {
    [pick0, pick1] = sumA <= sumB ? [A0, A1] : [B0, B1];
  }

  // چک مجدد برخورد
  const pickedCross = segIntersect(to2(pick0.from), to2(pick0.to), to2(pick1.from), to2(pick1.to));
  if (pickedCross) {
    const pickedIsA = pick0 === A0 || pick0 === A1 || pick1 === A0 || pick1 === A1;
    [pick0, pick1] = pickedIsA ? [B0, B1] : [A0, A1];
  }

  // تعیین راست و چپ بر اساس X در ENU
  const pick0From2 = to2(pick0.from);
  const pick1From2 = to2(pick1.from);
  let right, left;
  if (pick0From2.x >= pick1From2.x) {
    right = pick0;
    left = pick1;
  } else {
    right = pick1;
    left = pick0;
  }

  return { right, left, all: links };
};

const rectCornersFromWindow = (viewer, startWin, endWin) => {
  if (!viewer || !startWin || !endWin) return [];

  const x1 = Math.min(startWin.x, endWin.x);
  const y1 = Math.min(startWin.y, endWin.y);
  const x2 = Math.max(startWin.x, endWin.x);
  const y2 = Math.max(startWin.y, endWin.y);

  const w1 = new Cartesian2(x1, y1);
  const w2 = new Cartesian2(x2, y1);
  const w3 = new Cartesian2(x2, y2);
  const w4 = new Cartesian2(x1, y2);

  const c1 = windowPosToCartesian(viewer, w1);
  const c2 = windowPosToCartesian(viewer, w2);
  const c3 = windowPosToCartesian(viewer, w3);
  const c4 = windowPosToCartesian(viewer, w4);

  const pts = [c1, c2, c3, c4].filter(Boolean);
  if (pts.length < 3) return [];

  // Close outline
  const closed = [c1, c2, c3, c4, c1].filter(Boolean);
  return closed.length >= 4 ? closed : pts;
};

let _state = null;

let _undoStack = [];
const UNDO_MAX = 30;

const pushDrivewayUndo = (entry) => {
  if (!entry) return;
  _undoStack.push(entry);
  if (_undoStack.length > UNDO_MAX) _undoStack.shift();
};

export function undoDrivewayTool() {
  const st = _state;
  const viewer = st?.viewer;
  if (!st || !viewer) return false;

  const removePersistedVisual = () => {
    const ent = st.persistStrokeEntities?.pop?.();
    if (!ent) return;
    try {
      viewer.entities.remove(ent);
    } catch {}
  };

  const undoSelectionStroke = (which) => {
    const strokesKey = which === 2 ? "sel2Strokes" : "sel1Strokes";
    const pairsKey = which === 2 ? "sel2Pairs" : "sel1Pairs";

    const strokes = st[strokesKey] || [];
    const pairs = st[pairsKey] || [];

    if (!strokes.length) return false;

    const lastStroke = strokes.pop();
    const nPairs = lastStroke?.pairs?.length || 0;
    if (nPairs > 0) pairs.splice(Math.max(0, pairs.length - nPairs), nPairs);

    st[strokesKey] = strokes;
    st[pairsKey] = pairs;

    removePersistedVisual();
    return true;
  };

  const removeEntitiesByNumericIds = (ds, ids) => {
    if (!ds || !Array.isArray(ids) || !ids.length) return 0;
    const set = new Set(ids.filter((x) => Number.isFinite(x)));
    if (!set.size) return 0;

    let removed = 0;
    const ents = ds?.entities?.values || [];
    for (let i = ents.length - 1; i >= 0; i--) {
      const e = ents[i];
      const n = getNumericPropValue(e, "id");
      if (!set.has(n)) continue;
      try {
        ds.entities.remove(e);
        removed++;
      } catch {}
    }
    return removed;
  };

  // 1) Undo last selection area
  if (st.phase === 2) {
    if (undoSelectionStroke(2)) {
      showToast("Undo: removed last area (Selection 2).", Type.NOTIFICATION.INFO, { duration: 2000 });
      return true;
    }

    // If Selection 2 empty, go back to phase 1
    if (!st.sel2Strokes?.length) {
      st.phase = 1;
      showToast("Undo: back to Selection 1.", Type.NOTIFICATION.INFO, { duration: 2000 });
      return true;
    }
  }

  if (st.phase === 1) {
    if (undoSelectionStroke(1)) {
      showToast("Undo: removed last area (Selection 1).", Type.NOTIFICATION.INFO, { duration: 2000 });
      return true;
    }
  }

  // 2) Undo last created lines/lanelets (after build)
  const last = (_undoStack.length ? _undoStack.pop() : null) || st.lastCreated;

  if (last?.lineDs && Array.isArray(last?.lineNumericIds) && last.lineNumericIds.length) {
    const removedLines = removeEntitiesByNumericIds(last.lineDs, last.lineNumericIds);
    const removedLanelets = removeEntitiesByNumericIds(last.laneletDs, last.laneletNumericIds);

    try {
      st.onEdit?.({
        changedDataSources: [last.lineDs, ...(removedLanelets ? [last.laneletDs] : [])].filter(Boolean),
        changedLayerKeys: [last.lineKind === "virtual" ? "virtualline" : "turningline", ...(removedLanelets ? ["lanelet"] : [])],
      });
    } catch {}

    st.lastCreated = null;

    if (removedLines || removedLanelets) {
      showToast("Undo: removed last created entities.", Type.NOTIFICATION.INFO, { duration: 2500 });
      return true;
    }
  }

  return false;
}

export function toggleDrivewayTool(viewer, onEdit, disable, turningLineDs, virtualLineDs, laneMarkerDs, laneletDs, userData, mapObjectsRef = null, setMapObjects = null) {
  if (!viewer?.scene?.canvas) return;

  if (disable) {
    try {
      if (_state?.onWindowMouseUp && typeof window !== "undefined") window.removeEventListener("mouseup", _state.onWindowMouseUp);
      if (_state?.onKeyDown && typeof window !== "undefined") window.removeEventListener("keydown", _state.onKeyDown);

      try {
        if (_state?.livePencilEntity) viewer.entities.remove(_state.livePencilEntity);
      } catch {}
      try {
        if (_state?.liveRectEntity) viewer.entities.remove(_state.liveRectEntity);
      } catch {}

      try {
        if (Array.isArray(_state?.persistStrokeEntities)) {
          for (let i = 0; i < _state.persistStrokeEntities.length; i++) {
            try {
              viewer.entities.remove(_state.persistStrokeEntities[i]);
            } catch {}
          }
        }
      } catch {}

      try {
        restoreCameraControls(viewer, _state.cameraCache);
      } catch {}

      try {
        _state?.handler?.destroy();
      } catch {}

      _state = null;
    } catch {
      _state = null;
    }
    return;
  }

  const handler = new ScreenSpaceEventHandler(viewer.scene.canvas);

  const st = {
    viewer,
    handler,
    turningLineDs,
    virtualLineDs,
    laneMarkerDs,
    laneletDs,
    mapObjectsRef,
    setMapObjects,

    onEdit,
    lastCreated: null,

    turningLineStyle: pickPolylineStyleFromDataSource(turningLineDs),
    laneletLineStyle: pickPolylineStyleFromDataSource(laneletDs),
    virtualLineStyle: pickPolylineStyleFromDataSource(virtualLineDs),

    phase: 1, // 1: selection1, 2: selection2

    // Default mode: rectangle
    drawMode: MODE_RECT,

    // Drawing state
    isDrawing: false,
    drawRawPoints: [], // used in pencil mode
    lastDrawPoint: null, // used in pencil mode
    rectStartWin: null, // used in rect mode
    rectEndWin: null, // used in rect mode

    // Live visuals
    livePencilEntity: null,
    liveRectEntity: null,
    persistStrokeEntities: [],

    // Endpoint cache
    endpointCache: null,
    endpointCacheStamp: { vCount: 0, mCount: 0, lastMs: 0 },

    // Selections
    sel1Strokes: [],
    sel2Strokes: [],
    sel1Pairs: [],
    sel2Pairs: [],

    chordPrefix: null,
    chordTs: 0,

    cameraCache: { current: null },
    userData: userData,

    onWindowMouseUp: null,
    onKeyDown: null,
  };

  _state = st;

  const toastStep = () => {
    const modeText = st.drawMode === MODE_RECT ? "RECT" : "PENCIL";
    if (st.phase === 1) showToast(`Selection 1 (${modeText}): draw area. Press Enter when done.`, Type.NOTIFICATION.INFO, { duration: 4500 });
    else showHotkeysHelp();
  };

  // Help toast (throttled)
  const showHotkeysHelp = () => {
    const now = safeNowMs();
    if (st._helpToastLastMs && now - st._helpToastLastMs < 2500) return;
    st._helpToastLastMs = now;

    const modeText = st.drawMode === MODE_RECT ? "RECT" : "PENCIL";

    if (st.phase === 1) {
      return;
    }

    showToast(
      `Selection 2 (${modeText}): draw area.\n\n` +
        `Build:\n` +
        `- V: Virtual lines (NO lanelet)\n` +
        `- T: Turning lines (NO lanelet)\n` +
        `- Ctrl+Y: Turning + Lanelet (auto turn_direction)\n` +
        `- Ctrl+L: Turning + Lanelet (turn_direction=left)\n` +
        `- Ctrl+R: Turning + Lanelet (turn_direction=right)\n\n` +
        `Virtual + Lanelet (turn_direction fixed):\n` +
        `- Ctrl+S then L: split_left\n` +
        `- Ctrl+S then R: split_right\n` +
        `- Ctrl+M then L: merge_left\n` +
        `- Ctrl+M then R: merge_right\n` +
        `- Ctrl+E then L: exit_left\n` +
        `- Ctrl+E then R: exit_right\n\n` +
        `Esc: cancel current stroke.`,
      Type.NOTIFICATION.INFO,
      { duration: 9000 },
    );
  };

  const ensureEndpointCache = (force = false) => {
    const nowMs = safeNowMs();

    const vCount = st.virtualLineDs?.entities?.values?.length || 0;
    const mCount = st.laneMarkerDs?.entities?.values?.length || 0;

    const stamp = st.endpointCacheStamp || { vCount: 0, mCount: 0, lastMs: 0 };

    const changed = vCount !== stamp.vCount || mCount !== stamp.mCount;
    const tooSoon = nowMs - stamp.lastMs < ENDPOINT_CACHE_REBUILD_MS;

    if (!force && st.endpointCache && !changed) return;
    if (!force && changed && tooSoon && st.endpointCache) return;

    st.endpointCache = buildEndpointCache([st.virtualLineDs, st.laneMarkerDs]);
    st.endpointCacheStamp = { vCount, mCount, lastMs: nowMs };
  };

  const ensureLivePencil = () => {
    if (st.livePencilEntity) return;

    st.livePencilEntity = viewer.entities.add({
      polyline: {
        positions: new CallbackProperty(() => {
          const raw = st.drawRawPoints;
          if (!Array.isArray(raw) || raw.length < 2) return raw || [];
          return chaikinSmooth(raw, SMOOTH_ITERATIONS_VISUAL);
        }, false),
        clampToGround: true,
        width: 2,
        material: Color.YELLOW.withAlpha(0.9),
      },
    });
  };

  const ensureLiveRect = () => {
    if (st.liveRectEntity) return;

    st.liveRectEntity = viewer.entities.add({
      polyline: {
        positions: new CallbackProperty(() => {
          const pts = rectCornersFromWindow(st.viewer, st.rectStartWin, st.rectEndWin);
          return Array.isArray(pts) ? pts : [];
        }, false),
        clampToGround: true,
        width: 2,
        material: Color.YELLOW.withAlpha(0.9),
      },
    });
  };

  const removeLiveVisuals = () => {
    if (st.livePencilEntity) {
      try {
        viewer.entities.remove(st.livePencilEntity);
      } catch {}
      st.livePencilEntity = null;
    }
    if (st.liveRectEntity) {
      try {
        viewer.entities.remove(st.liveRectEntity);
      } catch {}
      st.liveRectEntity = null;
    }
  };

  const clearPersistedAreasAndSelections = () => {
    // remove persisted visuals
    try {
      const arr = st.persistStrokeEntities || [];
      for (let i = 0; i < arr.length; i++) {
        try {
          viewer.entities.remove(arr[i]);
        } catch {}
      }
    } catch {}

    st.persistStrokeEntities = [];

    // clear selections
    st.sel1Strokes = [];
    st.sel2Strokes = [];
    st.sel1Pairs = [];
    st.sel2Pairs = [];

    // reset phase
    st.phase = 1;
  };

  const addPersistedAreaVisual = (rawPts, modeAtDrawEnd) => {
    if (!Array.isArray(rawPts) || rawPts.length < 2) return;

    const mat = st.phase === 1 ? Color.ORANGE.withAlpha(0.75) : Color.CYAN.withAlpha(0.75);

    // Pencil: keep old smoothing look
    if (modeAtDrawEnd === MODE_PENCIL) {
      const dec = decimateByDistance(rawPts, PERSIST_DECIMATE_STEP_METERS);
      const smooth = chaikinSmooth(dec, PERSIST_SMOOTH_ITERS);

      const ent = viewer.entities.add({
        polyline: {
          positions: smooth,
          clampToGround: true,
          width: 2,
          material: mat,
        },
      });
      st.persistStrokeEntities.push(ent);
      return;
    }

    // Rect: show sharp rectangle
    const ent = viewer.entities.add({
      polyline: {
        positions: rawPts,
        clampToGround: true,
        width: 2,
        material: mat,
      },
    });
    st.persistStrokeEntities.push(ent);
  };

  const resetCurrentStroke = () => {
    st.isDrawing = false;

    st.drawRawPoints = [];
    st.lastDrawPoint = null;

    st.rectStartWin = null;
    st.rectEndWin = null;

    removeLiveVisuals();
    restoreCameraControls(viewer, st.cameraCache);
    if (viewer?.scene?.requestRenderMode) viewer.scene.requestRender();
  };

  const failCurrentStroke = (msg) => {
    showToast(msg, Type.NOTIFICATION.ERROR, { duration: 4500 });
    resetCurrentStroke();
  };

  const selectionPairsCount = (which) => (which === 1 ? st.sel1Pairs.length : st.sel2Pairs.length);

  const pickLanePointsFromPolygonStroke = (strokeRawPts) => {
    ensureEndpointCache(false);

    const decimated = decimateByDistance(strokeRawPts, POLY_DECIMATE_STEP_METERS);
    if (!Array.isArray(decimated) || decimated.length < 3) return [];

    const local = makeStrokeLocal2D(decimated);
    if (!local) return [];

    const { inv, segs, pts2, bbox } = local;

    const poly = normalizePolygonPoints(pts2);
    if (!Array.isArray(poly) || poly.length < 4) return [];

    const minx = bbox.minx - POLY_BBOX_PAD_METERS;
    const miny = bbox.miny - POLY_BBOX_PAD_METERS;
    const maxx = bbox.maxx + POLY_BBOX_PAD_METERS;
    const maxy = bbox.maxy + POLY_BBOX_PAD_METERS;

    const scratch = new Cartesian3();
    const res = [];

    const items = st.endpointCache || [];
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      const pt = it?.point;
      if (!pt) continue;

      Matrix4.multiplyByPoint(inv, pt, scratch);
      const x = scratch.x;
      const y = scratch.y;

      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
      if (x < minx || x > maxx || y < miny || y > maxy) continue;
      if (!pointInPolygon2D(x, y, poly)) continue;

      const pr = projectPointToStroke(x, y, segs);
      res.push({ ...it, dist: pr.dist, s: pr.s });
    }

    let deduped = dedupeCandidatesByPoint(res);

    deduped.sort((a, b) => {
      const sa = Number.isFinite(a?.s) ? a.s : Infinity;
      const sb = Number.isFinite(b?.s) ? b.s : Infinity;
      if (sa !== sb) return sa - sb;

      const da = Number.isFinite(a?.dist) ? a.dist : Infinity;
      const db = Number.isFinite(b?.dist) ? b.dist : Infinity;
      return da - db;
    });

    if (deduped.length > MAX_POINTS_PER_STROKE) {
      const keep = [];
      const step = (deduped.length - 1) / (MAX_POINTS_PER_STROKE - 1);
      for (let i = 0; i < MAX_POINTS_PER_STROKE; i++) {
        const idx = Math.round(i * step);
        keep.push(deduped[Math.min(deduped.length - 1, Math.max(0, idx))]);
      }
      deduped = dedupeCandidatesByPoint(keep);
      deduped.sort((a, b) => {
        const sa = Number.isFinite(a?.s) ? a.s : Infinity;
        const sb = Number.isFinite(b?.s) ? b.s : Infinity;
        if (sa !== sb) return sa - sb;
        const da = Number.isFinite(a?.dist) ? a.dist : Infinity;
        const db = Number.isFinite(b?.dist) ? b.dist : Infinity;
        return da - db;
      });
    }

    return deduped;
  };

  const addStrokeToSelection = (strokePoints) => {
    const pairs = makePairsFromOrderedPoints(strokePoints);
    if (pairs.length < 1) return { ok: false, reason: "Need at least 2 points in selection." };

    const strokeObj = { points: strokePoints, pairs };

    if (st.phase === 1) {
      st.sel1Strokes.push(strokeObj);
      st.sel1Pairs.push(...pairs);
      return { ok: true, selection: 1, strokeObj };
    }

    st.sel2Strokes.push(strokeObj);
    st.sel2Pairs.push(...pairs);
    return { ok: true, selection: 2, strokeObj };
  };

  const finalizeAndBuild = (opts = {}) => {
    const virtualTurnDirections = new Set(["split_left", "split_right", "merge_left", "merge_right", "exit_left", "exit_right"]);
    const requestedTurnDirection = typeof opts?.turnDirection === "string" && opts.turnDirection.length ? opts.turnDirection : null;
    const forceVirtual = opts?.lineKind === "virtual" || virtualTurnDirections.has(requestedTurnDirection);
    const lineKind = forceVirtual ? "virtual" : "turning";
    const createLanelet = opts?.createLanelet === false ? false : true;
    const fixedTurnDirection = requestedTurnDirection;

    const lineDs = lineKind === "virtual" ? st.virtualLineDs : st.turningLineDs;
    if (!lineDs) {
      showToast("Missing line datasource.", Type.NOTIFICATION.ERROR, { duration: 5000 });
      return;
    }

    const p1 = selectionPairsCount(1);
    const p2 = selectionPairsCount(2);

    if (p1 < 1 || p2 < 1) {
      showToast("Both selections must have at least 1 pair.", Type.NOTIFICATION.ERROR, { duration: 5000 });
      return;
    }

    if (p1 > 1 && p2 > 1) {
      showToast("Rule: at least one selection must have exactly 1 pair.", Type.NOTIFICATION.ERROR, { duration: 6000 });
      return;
    }

    const depPairs = st.sel1Pairs;
    const arrPairs = st.sel2Pairs;

    const baseStyle = lineKind === "virtual" ? st.virtualLineStyle || st.turningLineStyle : st.turningLineStyle;

    const created = [];
    const laneletsCreated = [];

    const createdLineNumericIds = [];
    const createdLaneletNumericIds = [];

    const pushCreatedLaneletId = (laneletEnt) => {
      try {
        const v = laneletEnt?.properties?.id?.getValue?.(timeNow());
        if (Number.isFinite(v)) createdLaneletNumericIds.push(v);
      } catch {}
    };

    const addLineEntity = lineKind === "virtual" ? addVirtualLineEntity : addTurningLineEntity;
    const rightName = lineKind === "virtual" ? "virtual_line_right" : "turning_line_right";
    const leftName = lineKind === "virtual" ? "virtual_line_left" : "turning_line_left";

    const tryCreateLaneletFromPair = (rightLineEntity, leftLineEntity, rightLineId, leftLineId, depPair, arrPair) => {
      if (!createLanelet) return null;
      if (!st.laneletDs) return null;
      if (!rightLineEntity || !leftLineEntity) return null;

      const laneletNextId = getNextIdPlus2(st.laneletDs, st);
      if (!Number.isFinite(laneletNextId)) return null;

      const rightPos = getPolylinePositions(rightLineEntity) || null;
      const leftPos = getPolylinePositions(leftLineEntity) || null;
      if (!rightPos || !leftPos) return null;

      const mid = computeMidlineBetweenTwoSegments(rightPos, leftPos);
      if (!mid || mid.length < 2) return null;

      const turnDir = fixedTurnDirection || getTurnDirectionFromTwoLines(rightPos, leftPos, depPair, arrPair);

      const laneletEnt = addLaneletCenterLineEntity(st.laneletDs, mid[0], mid[1], laneletNextId, rightLineId, leftLineId, turnDir, lineKind, st.laneletLineStyle);

      return laneletEnt;
    };

    try {
      if (depPairs.length === 1 && arrPairs.length === 1) {
        const mm = minMaxLinksBetweenTwoPairs(depPairs[0], arrPairs[0]);

        if (!mm) {
          showToast(lineKind === "virtual" ? "Failed to compute virtual lines." : "Failed to compute turning lines.", Type.NOTIFICATION.ERROR, { duration: 5000 });
          return;
        }

        const rightId = getNextIdPlus2(lineDs, st);
        const leftId = getNextIdPlus2(lineDs, st);
        createdLineNumericIds.push(rightId, leftId);
        let tdForTurning =
          lineKind === "turning"
            ? fixedTurnDirection === "left" || fixedTurnDirection === "right"
              ? fixedTurnDirection
              : getTurnDirectionFromTwoLines([mm.right.from, mm.right.to], [mm.left.from, mm.left.to], depPairs[0], arrPairs[0])
            : null;

        const rightTL = addLineEntity(lineDs, mm.right.from, mm.right.to, rightName, rightId, baseStyle, tdForTurning);
        const leftTL = addLineEntity(lineDs, mm.left.from, mm.left.to, leftName, leftId, baseStyle, tdForTurning);

        const laneletMid = tryCreateLaneletFromPair(rightTL, leftTL, rightId, leftId, depPairs[0], arrPairs[0]);

        if (laneletMid) laneletsCreated.push(laneletMid);
        if (laneletMid) pushCreatedLaneletId(laneletMid);

        created.push({ rightTL, leftTL, laneletMid, depPair: depPairs[0], arrPair: arrPairs[0], mm });
      } else if (depPairs.length === 1) {
        const depPair = depPairs[0];
        for (let i = 0; i < arrPairs.length; i++) {
          const arrPair = arrPairs[i];
          const mm = minMaxLinksBetweenTwoPairs(depPair, arrPair);
          if (!mm) continue;

          const rightId = getNextIdPlus2(lineDs, st);
          const leftId = getNextIdPlus2(lineDs, st);
          createdLineNumericIds.push(rightId, leftId);
          const tdForTurning =
            lineKind === "turning"
              ? fixedTurnDirection === "left" || fixedTurnDirection === "right"
                ? fixedTurnDirection
                : getTurnDirectionFromTwoLines([mm.right.from, mm.right.to], [mm.left.from, mm.left.to], depPair, arrPair)
              : null;

          const rightTL = addLineEntity(lineDs, mm.right.from, mm.right.to, rightName, rightId, baseStyle, tdForTurning);
          const leftTL = addLineEntity(lineDs, mm.left.from, mm.left.to, leftName, leftId, baseStyle, tdForTurning);

          const laneletMid = tryCreateLaneletFromPair(rightTL, leftTL, rightId, leftId, depPair, arrPair);

          if (laneletMid) laneletsCreated.push(laneletMid);
          if (laneletMid) pushCreatedLaneletId(laneletMid);

          created.push({ rightTL, leftTL, laneletMid, depPair, arrPair, mm });
        }
      } else {
        const arrPair = arrPairs[0];
        for (let i = 0; i < depPairs.length; i++) {
          const depPair = depPairs[i];
          const mm = minMaxLinksBetweenTwoPairs(depPair, arrPair);
          if (!mm) continue;

          const rightId = getNextIdPlus2(lineDs, st);
          const leftId = getNextIdPlus2(lineDs, st);
          createdLineNumericIds.push(rightId, leftId);
          const tdForTurning =
            lineKind === "turning"
              ? fixedTurnDirection === "left" || fixedTurnDirection === "right"
                ? fixedTurnDirection
                : getTurnDirectionFromTwoLines([mm.right.from, mm.right.to], [mm.left.from, mm.left.to], depPair, arrPair)
              : null;

          const rightTL = addLineEntity(lineDs, mm.right.from, mm.right.to, rightName, rightId, baseStyle, tdForTurning);
          const leftTL = addLineEntity(lineDs, mm.left.from, mm.left.to, leftName, leftId, baseStyle, tdForTurning);

          const laneletMid = tryCreateLaneletFromPair(rightTL, leftTL, rightId, leftId, depPair, arrPair);

          if (laneletMid) laneletsCreated.push(laneletMid);
          if (laneletMid) pushCreatedLaneletId(laneletMid);

          created.push({ rightTL, leftTL, laneletMid, depPair, arrPair, mm });
        }
      }

      try {
        const changedDataSources = [lineDs];
        const changedLayerKeys = [lineKind === "virtual" ? "virtualline" : "turningline"];

        if (createdLaneletNumericIds.length) {
          changedDataSources.push(st.laneletDs);
          changedLayerKeys.push("lanelet");
        }

        onEdit?.({
          changedDataSources,
          changedLayerKeys,
          created: {
            lineKind,
            lineNumericIds: createdLineNumericIds,
            laneletNumericIds: createdLaneletNumericIds,
          },
        });
      } catch {}

      st.lastCreated = {
        lineKind,
        lineDs,
        laneletDs: st.laneletDs,
        lineNumericIds: createdLineNumericIds.slice(),
        laneletNumericIds: createdLaneletNumericIds.slice(),
      };

      if (createdLineNumericIds.length || createdLaneletNumericIds.length) {
        pushDrivewayUndo({
          lineKind,
          lineDs,
          laneletDs: st.laneletDs,
          lineNumericIds: createdLineNumericIds.slice(),
          laneletNumericIds: createdLaneletNumericIds.slice(),
          onEdit,
        });
      }

      if (lineKind === "virtual") showToast("Virtual lines created.", Type.NOTIFICATION.SUCCESS, { duration: 4000 });
      else showToast("Turning lines created.", Type.NOTIFICATION.SUCCESS, { duration: 4000 });

      clearPersistedAreasAndSelections();
      toastStep();
    } catch {
      showToast(lineKind === "virtual" ? "Failed to create virtual lines." : "Failed to create turning lines.", Type.NOTIFICATION.ERROR, { duration: 5000 });
    }
  };

  const finalizeStroke = (areaRawPts, modeAtDrawEnd) => {
    if (!Array.isArray(areaRawPts) || areaRawPts.length < 3) {
      failCurrentStroke("Draw a valid area.");
      return;
    }

    if (st.phase === 2) {
      const p1 = selectionPairsCount(1);
      if (p1 > 1 && st.sel2Strokes.length >= 1) {
        failCurrentStroke("Selection 2 is limited to ONE area because Selection 1 has multiple pairs.");
        return;
      }
    }

    ensureEndpointCache(true);

    const candidatesOrdered = pickLanePointsFromPolygonStroke(areaRawPts);

    if (candidatesOrdered.length < 2) {
      failCurrentStroke(`Invalid selection: found ${candidatesOrdered.length} point(s). Need at least 2 endpoints inside the area.`);
      return;
    }

    if (Cartesian3.distance(candidatesOrdered[0].point, candidatesOrdered[candidatesOrdered.length - 1].point) <= SAME_POINT_EPS_METERS) {
      failCurrentStroke("Invalid selection: points overlap. Draw again.");
      return;
    }

    const addRes = addStrokeToSelection(candidatesOrdered);
    if (!addRes.ok) {
      failCurrentStroke(addRes.reason || "Invalid selection.");
      return;
    }

    if (st.phase === 2) {
      const p1 = selectionPairsCount(1);
      if (p1 > 1) {
        const lastStroke = st.sel2Strokes[st.sel2Strokes.length - 1];
        const lastPairs = lastStroke?.pairs?.length || 0;
        if (lastPairs !== 1) {
          st.sel2Strokes.pop();
          if (lastPairs > 0) st.sel2Pairs.splice(Math.max(0, st.sel2Pairs.length - lastPairs), lastPairs);
          failCurrentStroke("Selection 2 must result in exactly 1 pair (exactly 2 endpoints inside the area).");
          return;
        }
      }
    }

    addPersistedAreaVisual(areaRawPts, modeAtDrawEnd);

    if (st.phase === 1) {
      showToast(`Added area to Selection 1. Total pairs: ${selectionPairsCount(1)}. Press Enter when done.`, Type.NOTIFICATION.SUCCESS, {
        duration: 2500,
      });
    } else {
      showToast(`Added area to Selection 2. Total pairs: ${selectionPairsCount(2)}. Press Enter to finish.`, Type.NOTIFICATION.SUCCESS, {
        duration: 2500,
      });
    }
  };

  const onWindowMouseUp = () => {
    if (!st.isDrawing) return;

    const modeAtEnd = st.drawMode;

    let raw = [];
    if (modeAtEnd === MODE_PENCIL) {
      raw = st.drawRawPoints.slice();
    } else {
      raw = rectCornersFromWindow(st.viewer, st.rectStartWin, st.rectEndWin);
    }

    resetCurrentStroke();
    finalizeStroke(raw, modeAtEnd);
  };

  if (typeof window !== "undefined") window.addEventListener("mouseup", onWindowMouseUp);
  st.onWindowMouseUp = onWindowMouseUp;

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      st.chordPrefix = null;
      st.chordTs = 0;
      showToast("Cancelled stroke.", Type.NOTIFICATION.INFO, { duration: 2000 });
      resetCurrentStroke();
      return;
    }

    const keyLower = String(e.key || "").toLowerCase();
    const nowMs = safeNowMs();

    const readyForBuild = () => {
      if (st.isDrawing) return false;
      if (st.phase !== 2) return false;

      const p2 = selectionPairsCount(2);
      if (p2 < 1) {
        showToast("Selection 2 is empty. Draw at least one valid area.", Type.NOTIFICATION.ERROR, { duration: 4000 });
        return false;
      }

      return true;
    };

    // chord timeout
    if (st.chordPrefix && nowMs - (st.chordTs || 0) > 2000) {
      st.chordPrefix = null;
      st.chordTs = 0;
    }

    // complete chord (Ctrl+S/M/E then L/R) => virtual + lanelet + fixed turn_direction
    if (st.chordPrefix && !e.ctrlKey && !e.altKey && !e.metaKey && (keyLower === "l" || keyLower === "r")) {
      if (!readyForBuild()) return;

      const map = {
        s: { l: "split_left", r: "split_right" },
        m: { l: "merge_left", r: "merge_right" },
        e: { l: "exit_left", r: "exit_right" },
      };

      const td = map?.[st.chordPrefix]?.[keyLower] || null;

      st.chordPrefix = null;
      st.chordTs = 0;

      if (!td) return;

      e.preventDefault?.();
      e.stopPropagation?.();

      finalizeAndBuild({ lineKind: "virtual", createLanelet: true, turnDirection: td });
      return;
    }

    // start chord prefixes: Ctrl+S / Ctrl+M / Ctrl+E
    if (e.ctrlKey && (keyLower === "s" || keyLower === "m" || keyLower === "e")) {
      if (!readyForBuild()) return;

      e.preventDefault?.();
      e.stopPropagation?.();

      st.chordPrefix = keyLower;
      st.chordTs = nowMs;

      showToast("Press L or R to confirm.", Type.NOTIFICATION.INFO, { duration: 1500 });
      return;
    }

    // Ctrl+T => turning + lanelet + auto direction (same as default build)
    if (e.ctrlKey && keyLower === "y") {
      if (!readyForBuild()) return;

      e.preventDefault?.();
      e.stopPropagation?.();

      finalizeAndBuild();
      return;
    }

    // Ctrl+L / Ctrl+R => turning + lanelet + forced left/right
    if (e.ctrlKey && (keyLower === "l" || keyLower === "r")) {
      if (!readyForBuild()) return;

      e.preventDefault?.();
      e.stopPropagation?.();

      finalizeAndBuild({ lineKind: "turning", createLanelet: true, turnDirection: keyLower === "l" ? "left" : "right" });
      return;
    }

    // V => virtual only (no lanelet)
    if (!e.ctrlKey && !e.altKey && !e.metaKey && keyLower === "v") {
      if (!readyForBuild()) return;

      finalizeAndBuild({ lineKind: "virtual", createLanelet: false });
      return;
    }

    // T => turning only (no lanelet)
    if (!e.ctrlKey && !e.altKey && !e.metaKey && keyLower === "t") {
      if (!readyForBuild()) return;

      finalizeAndBuild({ lineKind: "turning", createLanelet: false });
      return;
    }

    // Toggle draw mode with M (no modifier)
    if (!e.ctrlKey && !e.altKey && !e.metaKey && (e.key === "m" || e.key === "M")) {
      if (st.isDrawing) return;
      st.drawMode = st.drawMode === MODE_RECT ? MODE_PENCIL : MODE_RECT;
      showToast(`Mode: ${st.drawMode === MODE_RECT ? "Rectangle" : "Pencil"}`, Type.NOTIFICATION.INFO, { duration: 2000 });
      toastStep();
      return;
    }

    if (e.key === "Enter") {
      if (st.isDrawing) return;

      st.chordPrefix = null;
      st.chordTs = 0;

      if (st.phase === 1) {
        const p1 = selectionPairsCount(1);
        if (p1 < 1) {
          showToast("Selection 1 is empty. Draw at least one valid area.", Type.NOTIFICATION.ERROR, { duration: 4000 });
          return;
        }

        st.phase = 2;

        toastStep();
        return;
      }

      finalizeAndBuild();
    }
  };

  if (typeof window !== "undefined") window.addEventListener("keydown", onKeyDown);
  st.onKeyDown = onKeyDown;

  toastStep();

  handler.setInputAction((movement) => {
    const ok = disableCameraControls(viewer, st.cameraCache);
    if (!ok) return;

    const p = movement?.position || movement;
    if (!p) {
      restoreCameraControls(viewer, st.cameraCache);
      return;
    }

    // Start drawing per mode
    st.isDrawing = true;

    if (st.drawMode === MODE_PENCIL) {
      const c = windowPosToCartesian(viewer, p);
      if (!c) {
        restoreCameraControls(viewer, st.cameraCache);
        st.isDrawing = false;
        return;
      }

      st.drawRawPoints = [c];
      st.lastDrawPoint = c;

      ensureLivePencil();
    } else {
      st.rectStartWin = new Cartesian2(p.x, p.y);
      st.rectEndWin = new Cartesian2(p.x, p.y);

      ensureLiveRect();
    }

    if (viewer?.scene?.requestRenderMode) viewer.scene.requestRender();
  }, ScreenSpaceEventType.LEFT_DOWN);

  handler.setInputAction((movement) => {
    if (!st.isDrawing) return;

    const p = movement?.endPosition || movement;
    if (!p) return;

    if (st.drawMode === MODE_PENCIL) {
      const c = windowPosToCartesian(viewer, p);
      if (!c) return;

      const last = st.lastDrawPoint;
      if (last && Cartesian3.distance(last, c) < DRAW_MIN_STEP_METERS) return;

      st.drawRawPoints.push(c);
      st.lastDrawPoint = c;

      if (st.drawRawPoints.length > DRAW_MAX_POINTS) {
        st.drawRawPoints.splice(0, st.drawRawPoints.length - DRAW_MAX_POINTS);
      }
    } else {
      st.rectEndWin = new Cartesian2(p.x, p.y);
    }

    if (viewer?.scene?.requestRenderMode) viewer.scene.requestRender();
  }, ScreenSpaceEventType.MOUSE_MOVE);

  handler.setInputAction(() => {
    if (!st.isDrawing) return;

    const modeAtEnd = st.drawMode;

    let raw = [];
    if (modeAtEnd === MODE_PENCIL) {
      raw = st.drawRawPoints.slice();
    } else {
      raw = rectCornersFromWindow(st.viewer, st.rectStartWin, st.rectEndWin);
    }

    resetCurrentStroke();
    finalizeStroke(raw, modeAtEnd);
  }, ScreenSpaceEventType.LEFT_UP);
}
